import type { FC, ReactNode } from 'react'
import type { FaqItem } from '@/types'

import Head from 'next/head'
import Link from 'next/link'
import { ArrowRight, Plus } from 'lucide-react'
import { buttonClassName } from '@/components/generals/button'
import { SectionHeading } from '@/components/landing/section-heading'

interface FaqSectionProps {
    items: FaqItem[]
    title?: ReactNode
    description?: ReactNode
    ctaHref?: string
}

export const FaqSection: FC<FaqSectionProps> = ({
    items,
    title = 'Dúvidas frequentes sobre consórcio',
    description = 'As respostas para o que mais perguntam antes de entrar em um grupo.',
    ctaHref = '#contato'
}): ReactNode => {
    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer
            }
        }))
    }

    return (
        <section id='duvidas' aria-labelledby='duvidas-titulo' className='border-t border-border py-24 sm:py-32'>
            <Head>
                <script
                    key='faq-structured-data'
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
                />
            </Head>

            <div className='mx-auto grid max-w-7xl items-start gap-12 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20'>
                <div className='lg:sticky lg:top-28'>
                    <SectionHeading
                        id='duvidas-titulo'
                        eyebrow='Dúvidas frequentes'
                        title={title}
                        description={description}
                    />

                    <div className='mt-10 rounded-[1.5rem] bg-ink p-7 text-primary-foreground'>
                        <p className='font-heading text-3xl leading-tight'>
                            Ainda ficou com dúvida?
                        </p>

                        <p className='mt-2 text-sm leading-relaxed text-primary-foreground/75'>
                            Um especialista explica cada detalhe do grupo antes de você decidir.
                        </p>

                        <Link href={ctaHref} className={buttonClassName({ variant: 'gold', className: 'mt-6' })}>
                            Falar com um especialista
                            <ArrowRight aria-hidden='true' className='size-4' />
                        </Link>
                    </div>
                </div>

                <div className='divide-y divide-border border-y border-border'>
                    {items.map(item => (
                        <details key={item.question} className='group'>
                            <summary className='flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-left text-lg font-semibold text-foreground transition-colors hover:text-bronze focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring [&::-webkit-details-marker]:hidden'>
                                <span>
                                    {item.question}
                                </span>

                                <span
                                    aria-hidden='true'
                                    className='grid size-9 shrink-0 place-items-center rounded-full border border-border bg-card text-foreground transition-all duration-300 group-open:rotate-45 group-open:border-gold group-open:bg-gold group-open:text-gold-foreground'
                                >
                                    <Plus className='size-4' />
                                </span>
                            </summary>

                            <p className='max-w-2xl pb-7 pr-12 leading-relaxed text-muted-foreground'>
                                {item.answer}
                            </p>
                        </details>
                    ))}
                </div>
            </div>
        </section>
    )
}
